import React, { useEffect, useState } from 'react';
import '../../styles/QueryProgress.css';

/** Seconds since `startedAt`, ticking once a second while mounted. */
function useElapsed(startedAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;
}

/**
 * Shown while a report query runs.
 *  - variant "block": nothing to show yet; a spinner with the elapsed time.
 *  - variant "bar": results are on screen and being refreshed; a thin
 *    progress bar across the top of the container (which must be positioned).
 * `retrying` is set when the engine timed out and the query is being polled
 * again; the engine keeps working on it in the meantime.
 */
const QueryProgress = ({ startedAt, retrying = false, variant = 'block', dark = true }) => {
  const elapsed = useElapsed(startedAt);
  if (variant === 'bar') {
    return (
      <div className="query-progress-bar" role="status" aria-label={`Refreshing${elapsed >= 2 ? `, ${elapsed} seconds` : ''}`}>
        <span />
      </div>
    );
  }
  return (
    <div className={`query-progress ${dark ? 'query-progress--dark' : ''}`} role="status" aria-live="polite">
      <span className="query-progress-spinner" aria-hidden="true" />
      <span className="query-progress-text">
        {retrying ? 'Still running' : 'Running query'}
        {elapsed >= 2 && <span className="query-progress-time"> · {elapsed}s</span>}
      </span>
      {(retrying || elapsed >= 10) && (
        <span className="query-progress-hint">
          {retrying ? 'The query is taking longer than usual. Results appear here when it finishes.' : 'Large datasets take longer the first time a report runs.'}
        </span>
      )}
    </div>
  );
};

export default QueryProgress;
